import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DateRangePicker, { calcDatePreset } from '../fields/DateRangePicker';
import PaginationComponent from '../PaginationComponent';
import { pointAPI } from '../../services';

// #221938 — Lịch sử ĐIỂM THƯỞNG/PHẠT của 1 nhân viên (bảng Point).
// KHÁC `EmployeePointHistoryTab` (lịch sử TỈ LỆ thưởng doanh thu, đơn vị %): tab này là số ĐIỂM,
// gộp đủ mọi nguồn cộng/trừ — chấm tay ở /points, xử lý QC ở /qc-results, và hệ thống tự sinh.

const LIMIT = 10;

const fmtPoint = (n) => `${n > 0 ? '+' : ''}${Number(n).toFixed(2).replace(/\.00$/, '')}`;
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '-');

const TYPE_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'ADD', label: 'Cộng điểm' },
  { value: 'SUBTRACT', label: 'Trừ điểm' },
];

// Gom nguồn về 3 nhóm sếp hỏi: chấm tay / QC / hệ thống.
const sourceKindOf = (row) => {
  if (row.source_type === 'MANUAL') return { text: 'Chấm tay', bg: 'primary' };
  if (row.source_type === 'QC_RESULT') return { text: 'QC', bg: 'info' };
  return { text: 'Hệ thống', bg: 'secondary' };
};

// "Ai chấm": chỉ hiện tên khi backend thật sự có dữ liệu người chấm; dòng cũ (trước #221938)
// không lưu người chấm nên nói thẳng "không ghi nhận" thay vì bịa tên.
const actorTextOf = (row) => {
  if (row.actor_name) return row.actor_name;
  if (row.actor_kind === 'SYSTEM') return 'Hệ thống tự động';
  return 'Không ghi nhận';
};

const STATUS_BADGE = {
  CANCELLED: { text: 'Đã huỷ', bg: 'dark' },
  APPEALED: { text: 'Đang khiếu nại', bg: 'warning' },
  PENDING: { text: 'Treo chờ', bg: 'warning' },
};

export default function EmployeeRewardPointHistoryTab({ employeeId }) {
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [typeValue, setTypeValue] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchHistory = useCallback(async () => {
    if (!employeeId) return;
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: LIMIT };
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      if (typeValue) params.type = typeValue;
      const result = await pointAPI.getEmployeeRewardPointHistory(employeeId, params);
      if (requestIdRef.current !== requestId) return;
      setRows(result?.data || []);
      setTotal(result?.total || 0);
      setTotals(result?.totals || null);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      const message = err?.message || 'Không thể tải lịch sử điểm thưởng';
      toast.error(message);
      setError(message);
      setRows([]);
      setTotal(0);
      setTotals(null);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [employeeId, page, range, typeValue]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleRangeChange = (from, to) => {
    setRange({ from, to });
    setPage(1);
  };

  const handleTypeChange = (e) => {
    setTypeValue(e.target.value);
    setPage(1);
  };

  return (
    <div>
      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-8">
          <DateRangePicker
            fromDate={range.from}
            toDate={range.to}
            onChange={handleRangeChange}
            defaultPreset="last3months"
          />
        </div>
        <div className="col-md-4">
          <Form.Group controlId="reward-point-history-type">
            <Form.Label>Loại</Form.Label>
            <Form.Select size="sm" value={typeValue} onChange={handleTypeChange}>
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {/* Tổng cộng dồn TOÀN SỰ NGHIỆP (không đổi theo bộ lọc ngày) — khớp số của màn /points. */}
      {totals && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="success" className="p-2">
            Tổng cộng: +{Number(totals.total_add_points).toFixed(2).replace(/\.00$/, '')}
          </Badge>
          <Badge bg="danger" className="p-2">
            Tổng trừ: -{Number(totals.total_subtract_points).toFixed(2).replace(/\.00$/, '')}
          </Badge>
          <Badge bg="dark" className="p-2">
            Số dư hiện tại: {Number(totals.net_points).toFixed(2).replace(/\.00$/, '')} điểm
          </Badge>
          <span className="text-muted small align-self-center">
            (tính trên {totals.active_records} dòng còn hiệu lực, toàn sự nghiệp)
          </span>
        </div>
      )}

      {loading && (
        <div className="text-center py-4" role="status" aria-label="Đang tải">
          <Spinner size="sm" />
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger" className="py-2">{error}</Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <p className="text-muted text-center py-4 mb-0">
          Không có điểm thưởng/phạt nào trong khoảng thời gian này.
        </p>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* Màn rộng: bảng đủ cột */}
          <div className="d-none d-md-block">
            <Table bordered size="sm" responsive hover>
              <thead>
                <tr>
                  <th>Thời gian</th>
                  <th className="text-end">Điểm</th>
                  <th>Lý do</th>
                  <th>Nguồn</th>
                  <th>Ai chấm</th>
                  <th className="text-end">Số dư sau</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const kind = sourceKindOf(row);
                  const statusBadge = STATUS_BADGE[row.status];
                  return (
                    <tr key={row.id}>
                      <td className="small">{fmtDateTime(row.created_at)}</td>
                      <td className={`text-end fw-medium ${row.signed_value >= 0 ? 'text-success' : 'text-danger'}`}>
                        {fmtPoint(row.signed_value)}
                        {statusBadge && (
                          <div>
                            <Badge bg={statusBadge.bg} className="fw-normal">{statusBadge.text}</Badge>
                          </div>
                        )}
                      </td>
                      <td className="small">{row.reason || '-'}</td>
                      <td>
                        <Badge bg={kind.bg}>{kind.text}</Badge>
                        <div className="small text-muted">{row.source_label}</div>
                      </td>
                      <td className="small">{actorTextOf(row)}</td>
                      <td className="text-end fw-medium">
                        {row.balance_after === null ? '-' : Number(row.balance_after).toFixed(2).replace(/\.00$/, '')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Khổ điện thoại: mỗi dòng điểm là 1 thẻ xếp dọc, không phải bảng cuộn ngang */}
          <div className="d-md-none d-flex flex-column gap-2">
            {rows.map((row) => {
              const kind = sourceKindOf(row);
              const statusBadge = STATUS_BADGE[row.status];
              return (
                <div key={row.id} className="border rounded p-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <span className="small text-muted">{fmtDateTime(row.created_at)}</span>
                    <span className={`fw-bold ${row.signed_value >= 0 ? 'text-success' : 'text-danger'}`}>
                      {fmtPoint(row.signed_value)} điểm
                    </span>
                  </div>
                  <div className="mt-1">{row.reason || '-'}</div>
                  <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                    <Badge bg={kind.bg}>{kind.text}</Badge>
                    {statusBadge && <Badge bg={statusBadge.bg}>{statusBadge.text}</Badge>}
                    <span className="small text-muted">{row.source_label}</span>
                  </div>
                  <div className="small mt-1">
                    Ai chấm: <span className="fw-medium">{actorTextOf(row)}</span>
                  </div>
                  <div className="small">
                    Số dư sau:{' '}
                    <span className="fw-medium">
                      {row.balance_after === null ? '-' : Number(row.balance_after).toFixed(2).replace(/\.00$/, '')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <PaginationComponent
            totalItems={total}
            itemsPerPage={LIMIT}
            currentPage={page}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}
    </div>
  );
}
